
  import { defineComponent, toRef, computed } from 'vue';
  import Chart from '@/ui/components/analytics/displays/charts/Chart.vue';
  import { GUEST_EXTRA_HEIGHT, useCharts } from '@/ui/concerns/analytics/useCharts';
  import { ChartBasicSeries, ReportWidget } from '@/services/api/resources/analytics-builder/model';
  import { getAttributeValue, getChartAttribute } from '@/services/api/resources/analytics-builder/transform';
  import FormItemTitle from '@/ui/components/analytics/displays/FormItemTitle.vue';
  import { useWidgetFormItem } from '@/ui/concerns/analytics/widget/useWidgets';
  import { useMetrics } from '@/ui/concerns/analytics/widget';
  import { useReportInjection } from '@/ui/concerns/analytics/useReportInjection';

  interface LineChartProps {
    widget: ReportWidget;
    percentage: boolean;
    data: { labels: Array<string>; series: Array<ChartBasicSeries> };
  }

  const DEFAULT_CHART_HEIGHT = 233;
  const RESPONSE_CHART_HEIGHT = 199;

  export default defineComponent<LineChartProps>({
    components: {
      Chart,
      FormItemTitle,
    },
    props: ['percentage', 'widget', 'data'],
    setup(props, context) {
      const isDataPercentage = toRef(props, 'percentage');

      const { isGuest, reportId, report } = useReportInjection();
      const { findMetricLabel } = useMetrics();
      const {
        formatLabels,
        getDefaultChartConfig,
      } = useCharts(context, reportId.value ?? undefined, report.value, props.widget);
      const { isResponseAttributeSelected } = useWidgetFormItem(props.widget);

      const formatYLabels = (value: number) => formatLabels(value, isDataPercentage.value);

      const formatXLabels = (value: number) => getAttributeValue(
        value,
        getChartAttribute(props.widget.attribute),
        props.widget.attribute!,
      );

      const height = computed(() => {
        let px = isResponseAttributeSelected.value ? RESPONSE_CHART_HEIGHT : DEFAULT_CHART_HEIGHT;

        if (isGuest) {
          px += GUEST_EXTRA_HEIGHT;
        }

        return px;
      });

      const options = computed(() => getDefaultChartConfig(
        props.data.labels as string[],
        props.data.series.map(series => ({
          ...series,
          type: 'column',
          name: series.name ?? findMetricLabel(props.widget.metric!),
        })),
        formatXLabels,
        formatYLabels,
        'column',
        height.value,
        isDataPercentage.value,
        true,
      ));

      return {
        isResponseAttributeSelected,
        options,
      };
    },
  });
